const STATE_KEY = 'craftcloud.engine.handoff.state';
const VERIFIER_KEY = 'craftcloud.engine.handoff.verifier';
const RETURN_PATH_KEY = 'craftcloud.engine.handoff.returnPath';

function base64Url(bytes: Uint8Array): string {
  let binary = '';
  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });
  return window.btoa(binary)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

function randomValue(byteLength: number): string {
  const bytes = new Uint8Array(byteLength);
  window.crypto.getRandomValues(bytes);
  return base64Url(bytes);
}

export function safeReturnPath(value?: string | null): string {
  if (!value || !value.startsWith('/') || value.startsWith('//') || value.includes('://')) {
    return '/documents';
  }
  return value;
}

export async function createHandoffRequest(returnPath: string): Promise<{
  state: string;
  verifier: string;
  challenge: string;
  returnPath: string;
}> {
  const state = randomValue(32);
  const verifier = randomValue(32);
  const digest = await window.crypto.subtle.digest(
    'SHA-256',
    new TextEncoder().encode(verifier)
  );
  const challenge = base64Url(new Uint8Array(digest));
  const normalizedReturnPath = safeReturnPath(returnPath);
  window.sessionStorage.setItem(STATE_KEY, state);
  window.sessionStorage.setItem(VERIFIER_KEY, verifier);
  window.sessionStorage.setItem(RETURN_PATH_KEY, normalizedReturnPath);
  return { state, verifier, challenge, returnPath: normalizedReturnPath };
}

function constantTimeEqual(left: string, right: string): boolean {
  const length = Math.max(left.length, right.length);
  let mismatch = left.length ^ right.length;
  for (let index = 0; index < length; index += 1) {
    mismatch |= (left.charCodeAt(index) || 0) ^ (right.charCodeAt(index) || 0);
  }
  return mismatch === 0;
}

export function consumeHandoffState(receivedState: string): {
  verifier: string;
  returnPath: string;
} | null {
  const expectedState = window.sessionStorage.getItem(STATE_KEY) || '';
  const verifier = window.sessionStorage.getItem(VERIFIER_KEY) || '';
  const returnPath = safeReturnPath(window.sessionStorage.getItem(RETURN_PATH_KEY));
  window.sessionStorage.removeItem(STATE_KEY);
  window.sessionStorage.removeItem(VERIFIER_KEY);
  window.sessionStorage.removeItem(RETURN_PATH_KEY);
  if (!expectedState || !verifier || !constantTimeEqual(expectedState, receivedState)) {
    return null;
  }
  return { verifier, returnPath };
}

export const pkceStorageKeys = {
  state: STATE_KEY,
  verifier: VERIFIER_KEY,
  returnPath: RETURN_PATH_KEY,
};

